import { useCallback, useEffect, useRef, useState } from "react"

const SECRET_CODE = "dcflags"
const KEY_TIMEOUT_MS = 3000
const TAP_WINDOW_MS = 600
const REQUIRED_TAPS = 5

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')
  )
}

/** Cmd/Ctrl + Shift + comma. Shift makes `event.key` "<", so match `code`. */
const isSecretChord = (event: KeyboardEvent) => {
  if (event.repeat || event.altKey) return false
  if (!(event.metaKey || event.ctrlKey) || !event.shiftKey) return false
  return event.code === "Comma"
}

export const useSecretFeatureFlagsModal = () => {
  const [open, setOpen] = useState(false)
  const bufferRef = useRef("")
  const lastKeyTimeRef = useRef(0)
  const tapCountRef = useRef(0)
  const lastTapTimeRef = useRef(0)

  const openModal = useCallback(() => setOpen(true), [])
  const closeModal = useCallback(() => setOpen(false), [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isSecretChord(event)) {
        event.preventDefault()
        setOpen(true)
        return
      }

      if (isTypingTarget(event.target)) return
      if (event.metaKey || event.ctrlKey || event.altKey) return

      const now = Date.now()
      if (now - lastKeyTimeRef.current > KEY_TIMEOUT_MS) {
        bufferRef.current = ""
      }
      lastKeyTimeRef.current = now

      if (event.key.length !== 1) return

      bufferRef.current = `${bufferRef.current}${event.key.toLowerCase()}`.slice(-SECRET_CODE.length)
      if (bufferRef.current === SECRET_CODE) {
        bufferRef.current = ""
        setOpen(true)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  const handleSecretTap = useCallback(() => {
    const now = Date.now()
    if (now - lastTapTimeRef.current > TAP_WINDOW_MS) {
      tapCountRef.current = 0
    }
    lastTapTimeRef.current = now
    tapCountRef.current += 1

    if (tapCountRef.current >= REQUIRED_TAPS) {
      tapCountRef.current = 0
      setOpen(true)
    }
  }, [])

  return {
    open,
    openModal,
    closeModal,
    handleSecretTap,
  }
}
